import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
// TASK-66 PR-D: hook centralizes the union of teams.player1/2 +
// team_members.user_id (was UPPDRAG-62 PR-D's three-source union
// including captain_id; captain_id stripped — captain users still
// arrive via the team_members branch).
import { useMyTeamForLeague } from "@/hooks/useMyTeamForLeague";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy } from "lucide-react";
import LeagueInfoCard from "@/components/leagues/LeagueInfoCard";
import MySchedule from "@/components/leagues/MySchedule";
import StandingsTable from "@/components/leagues/StandingsTable";
import { useState } from "react";

const statusLabels: Record<string, string> = {
  draft: "Utkast",
  registration_open: "Anmälan öppen",
  active: "Pågår",
  completed: "Avslutad",
  archived: "Arkiverad",
};

interface LeagueDetailPanelProps {
  leagueId: string;
}

export default function LeagueDetailPanel({ leagueId }: LeagueDetailPanelProps) {
  const [showRegistration, setShowRegistration] = useState(false);

  const { data: league, isLoading } = useQuery({
    queryKey: ["league", leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leagues")
        .select("*, halls(name, address)")
        .eq("id", leagueId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!leagueId,
  });

  // TASK-66 PR-D: useMyTeamForLeague returns union of teams.player1/2 ∪
  // team_members.user_id, deduped. Captain users still resolve via the
  // team_members branch.
  const { team: myTeam } = useMyTeamForLeague(leagueId);

  const { data: teamCount } = useQuery({
    queryKey: ["league-team-count", leagueId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("teams")
        .select("*", { count: "exact", head: true })
        .eq("league_id", leagueId)
        .eq("status", "active");
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!leagueId,
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-4 w-full">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  if (!league) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground p-6">
        <p>Serien hittades inte</p>
      </div>
    );
  }

  const isOpen = league.status === "registration_open";
  const alreadyRegistered = !!myTeam;

  return (
    <div className="p-6 space-y-4 w-full overflow-y-auto">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-bold">{league.name}</h2>
          <Badge variant="secondary">{league.category}</Badge>
        </div>
        <Badge variant={isOpen ? "default" : "outline"}>
          {statusLabels[league.status] || league.status}
        </Badge>
      </div>

      {/* Tabs */}
      {alreadyRegistered && myTeam?.division_id ? (
        <Tabs defaultValue="info">
          <TabsList>
            <TabsTrigger value="info">Info</TabsTrigger>
            <TabsTrigger value="schedule">Schema</TabsTrigger>
            <TabsTrigger value="standings">Tabell</TabsTrigger>
          </TabsList>
          <TabsContent value="info">
            <LeagueInfoCard
              league={league}
              teamCount={teamCount}
              myTeam={myTeam}
              isOpen={isOpen}
              alreadyRegistered={alreadyRegistered}
              showRegistration={showRegistration}
              setShowRegistration={setShowRegistration}
            />
          </TabsContent>
          <TabsContent value="schedule">
            <MySchedule leagueId={league.id} teamId={myTeam.id} league={league} />
          </TabsContent>
          <TabsContent value="standings">
            <StandingsTable
              divisionId={myTeam.division_id}
              teamId={myTeam.id}
              promotionCount={league.promotion_count}
              relegationCount={league.relegation_count}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <LeagueInfoCard
          league={league}
          teamCount={teamCount}
          myTeam={myTeam}
          isOpen={isOpen}
          alreadyRegistered={alreadyRegistered}
          showRegistration={showRegistration}
          setShowRegistration={setShowRegistration}
        />
      )}
    </div>
  );
}
